import { mapValues } from "lodash";
import { calculateAge } from "Utils/dateHelpers";
import { formatNameFL } from "Utils/formatName";

import type { EmployeeApplicationState } from "EmployeeApplication/types";
import type { FamilyInfo } from "EmployeeCoverage/Utils/chooseCoverage";
import type { EmployeeForm, EmployeePolicy, EmployeeDependent, Segment } from "Utils/types";

export const getEmployeeHashId = (state: EmployeeApplicationState): string => state.employeeHashId;

export const employeePlanField = (type: string) => {
  if (type === "medical") {
    return "enrolledPlanID";
  }
  if (type === "dental") {
    return "enrolledDentalPlanID";
  }
  if (type === "vision") {
    return "enrolledVisionPlanID";
  }
  if (type === "accident") {
    return "enrolledAccidentPlanID";
  }
  if (type === "life") {
    return "enrolledLifePlanID";
  }
  if (type === "term_life") {
    return "enrolledTermLifePlanID";
  }
  if (type === "whole_life") {
    return "enrolledWholeLifePlanID";
  }
  if (type === "short_term_disability") {
    return "enrolledShortTermDisabilityPlanID";
  }
  if (type === "long_term_disability") {
    return "enrolledLongTermDisabilityPlanID";
  }
  throw new Error(`Unknown plan type: ${type}`);
};

export const employeeEnrollmentField = (type: string) => {
  if (type === "medical") {
    return "isEnrolling";
  }
  if (type === "dental") {
    return "isEnrollingDental";
  }
  if (type === "vision") {
    return "isEnrollingVision";
  }
  if (type === "accident") {
    return "isEnrollingAccident";
  }
  if (type === "life") {
    return "isEnrollingLife";
  }
  if (type === "term_life") {
    return "isEnrollingTermLife";
  }
  if (type === "whole_life") {
    return "isEnrollingWholeLife";
  }
  if (type === "short_term_disability") {
    return "isEnrollingShortTermDisability";
  }
  if (type === "long_term_disability") {
    return "isEnrollingLongTermDisability";
  }
  throw new Error(`Unknown plan type: ${type}`);
};

export const dependentEnrollmentField = (type: string) => {
  if (type === "medical") {
    return "dependentsEnrolling";
  }
  if (type === "dental") {
    return "dependentsEnrollingDental";
  }
  if (type === "vision") {
    return "dependentsEnrollingVision";
  }
  if (type === "accident") {
    return "dependentsEnrollingAccident";
  }
  if (type === "life") {
    return "dependentsEnrollingLife";
  }
  if (type === "term_life") {
    return "dependentsEnrollingTermLife";
  }
  if (type === "whole_life") {
    return "dependentsEnrollingWholeLife";
  }
  if (type === "long_term_disability") {
    return "dependentsEnrollingLongTermDisability";
  }
  if (type === "short_term_disability") {
    return "dependentsEnrollingShortTermDisability";
  }
  throw new Error(`Unknown plan type: ${type}`);
};

export const getEmployeeEnrollment = (employeePolicy: EmployeePolicy | null | undefined, type: string): ("Yes" | null | undefined) | "No" =>
  employeePolicy && employeePolicy[employeeEnrollmentField(type)];

export const getDependentEnrollment = (employeePolicy: EmployeePolicy | null | undefined, type: string): Record<string, "Yes" | "No"> => {
  if (!employeePolicy) {
    return {};
  }
  const field = employeePolicy[dependentEnrollmentField(type)];
  return mapValues(field, (v) => (v ? "Yes" : "No"));
};

export const applyContributionTweaks = (type: "medical" | "dental" | "vision", formula: ContributionFormula): ContributionFormula => {
  if (formula.type === "fixed" && type !== "medical") {
    // The server treats fixed contributions as applying to medical only, so show
    // the user no contributions for dental and vision.
    return { type: "fixed", employee: 0, dependent: 0 };
  }
  if (formula.type === "percent_of_base_plan" && type !== "medical") {
    // The key for percent_of_base_plan is always a medical plan ID, so this
    // doesn't apply for dental and vision.
    return {
      type: "percentage",
      employee: formula.employee,
      dependent: formula.dependent,
    };
  }
  return formula;
};

export const formatIndividual = (entity: EmployeeForm | EmployeeDependent) => ({
  name: formatNameFL(entity),
  age: entity.bornAt ? calculateAge(new Date(entity.bornAt)) : 0,
  type: (entity as any).dependentType || "employee",
  member_id: entity.id,
});

export const getEmployeeDependentsInProgress = (state: EmployeeApplicationState): Array<FamilyInfo> => {
  const employeeDependents = state.form.familyInformation ? state.form.familyInformation.values.dependents : [];
  return [...employeeDependents];
};

export const formattedAddress = (entity: EmployeeForm | null | undefined) => {
  if (entity) {
    const homeAddress = entity.homeAddress || "";
    const city = entity.city ? `, ${entity.city}` : "";
    const state = entity.state ? `, ${entity.state}` : "";
    const zipCode = entity.zipCode ? `, ${entity.zipCode}` : "";

    return homeAddress + city + state + zipCode;
  }
  return null;
};

export const formatHiddenSSID = (entity: EmployeeForm | null | undefined) => {
  if (entity && entity.socialSecurityNumber) {
    const ssid = entity.socialSecurityNumber;
    const lastFourDigits = ssid.slice(ssid.length - 4, ssid.length);
    return "XXX-XX-".concat(lastFourDigits);
  }
  return "";
};

export const getFormatedEmployeeType = (entity: EmployeeForm | null | undefined) => {
  if (entity) {
    const { employeeType } = entity;
    if (employeeType == "full_time") {
      return "Full time";
    }
    if (employeeType == "part_time") {
      return "Part time";
    }
    return employeeType;
  }
  return "";
};

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const getEnrollmentSegment = (
  employeePolicy: EmployeePolicy | null | undefined,
  employeeDependents: Array<EmployeeDependent>,
  type: "medical" | "dental" | "vision" | "accident" | "life" | "term_life" | "whole_life" | "short_term_disability" | "long_term_disability",
): Segment | null | undefined => {
  const employeeEnrollment = getEmployeeEnrollment(employeePolicy, type);
  const dependentEnrollment = getDependentEnrollment(employeePolicy, type);
  const spouseEnrolling = employeeDependents.some(
    (x) => (x.dependentType === "spouse" || x.dependentType === "domestic_partner") && dependentEnrollment[x.id] === "Yes",
  );

  const childEnrolling = employeeDependents.some((x) => x.dependentType === "child" && dependentEnrollment[x.id] === "Yes");

  if (employeeEnrollment === "No") {
    return "none";
  }
  if (!employeeEnrollment) {
    return null;
  }
  if (spouseEnrolling && childEnrolling) {
    return "ee_family";
  }
  if (spouseEnrolling) {
    return "ee_spouse";
  }
  if (childEnrolling) {
    return "ee_children";
  }
  return "ee_only";
};
